// src\modules\IntegratedMessaging\components\interaction\DropdownActions.tsx

import { faBagShopping, faEllipsis, faEye, faPen, faQrcode, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { useToast } from '@shared/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useRef } from 'react';
import { useDeleteProduct } from '../../hooks';
import { BarcodeModal, BarcodeModalRef } from '../modals/BarcodeModal';
import { DeleteModal, DeleteModalRef } from '../modals/DeleteModal';
import { LocationsStockModal, LocationsStockModalRef } from '../modals/LocationsStockModal';

interface DropdownActionsProps {
  product: any;
  refetch?: () => void;
}

export const DropdownActions = ({ product, refetch }: DropdownActionsProps) => {
  const { t } = useTranslation('productsModule');
  const { canAccess, hasPermission, getTeamType } = usePermissions();
  const deleteModalRef = useRef<DeleteModalRef>(null);
  const barcodeModalRef = useRef<BarcodeModalRef>(null);
  const locationsStockModalRef = useRef<LocationsStockModalRef>(null);
  const deleteProductMutation = useDeleteProduct();
  const { toast } = useToast();

  // 🎯 Solo tenant_3pl puede editar productos; tenant_external solo puede ver la información
  const canEdit = canAccess(['tenant_3pl'], ['products.edit']);
  const canViewProduct = getTeamType() === 'tenant_external' && hasPermission('products.edit');
  const canDelete = hasPermission('products.delete');
  // products.manage-stock solo disponible para tenant y tenant_3pl, NO para tenant_external
  const canManageStock = canAccess(['tenant', 'tenant_3pl'], ['products.manage-stock']);

  const handleBarcodeClick = () => {
    barcodeModalRef.current?.open({
      id: product.id,
      name: product.name,
      code: product.code,
      image: product.image_hash,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hover:bg-muted rounded-md p-2" data-testid="dropdown-trigger">
          <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-44 border-card-border" data-testid="dropdown-content">
        {/* Opción de código de barras - disponible para todos los usuarios */}
        <DropdownMenuItem onClick={handleBarcodeClick} data-testid="generate-barcode-action">
          <FontAwesomeIcon icon={faQrcode} className="mr-2 w-3.5" />
          {t('actions.generate_barcode')}
        </DropdownMenuItem>

        {/* Opción de ajustar stock - requiere permiso products.manage-stock */}
        {canManageStock && (
          <DropdownMenuItem
            onClick={() => locationsStockModalRef.current?.open(product.id)}
            data-testid="adjust-stock-action"
          >
            <FontAwesomeIcon icon={faBagShopping} className="mr-2 w-3.5" />
            {t('actions.adjust_stock')}
          </DropdownMenuItem>
        )}

        {/* Opción de editar producto - solo tenant_3pl */}
        {canEdit && (
          <DropdownMenuLinkItem to={`/products/edit/${product.id}`} data-testid="edit-product-action">
            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
            {t('actions.edit')}
          </DropdownMenuLinkItem>
        )}

        {/* Opción de ver producto - tenant_external (modo solo lectura) */}
        {canViewProduct && (
          <DropdownMenuLinkItem to={`/products/edit/${product.id}`} data-testid="view-product-action">
            <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5" />
            {t('actions.view')}
          </DropdownMenuLinkItem>
        )}

        {/* Opción de eliminar producto - requiere permiso products.delete */}
        {canDelete && (
          <DropdownMenuItem
            onClick={() =>
              deleteModalRef.current?.open(
                () =>
                  deleteProductMutation.mutate(product.id, {
                    onSuccess: () => {
                      toast({
                        title: t('toasts.delete_success'),
                      });
                      if (refetch) refetch();
                    },
                    onError: (error: any) => {
                      // 🎯 Extraer error_code del response del backend
                      const errorCode = error?.response?.data?.error?.error_code;

                      // 🎯 Determinar el mensaje de error según el error_code
                      let errorMessage = error?.message || t('toasts.delete_error');

                      if (errorCode === 'PRODUCT_IN_USE') {
                        errorMessage = t('toasts.delete_error_product_in_use');
                      }

                      toast({
                        title: t('toasts.error_title'),
                        description: errorMessage,
                        variant: 'destructive',
                      });
                    },
                  }),
                product.name,
              )
            }
            data-testid="delete-product-action"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
            {t('actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      <DeleteModal ref={deleteModalRef} />
      <BarcodeModal ref={barcodeModalRef} />
      <LocationsStockModal ref={locationsStockModalRef} refetch={refetch} />
    </DropdownMenu>
  );
};
